"use client";

import {
    ArrowRight,
    CalendarDays,
    GraduationCap,
    MapPin,
    Sparkles,
    TrendingUp,
} from "lucide-react";
import Image from "next/image";
import { useMemo, useState } from "react";

const events = [
    {
        id: "mgm",
        name: "MGM Medical College, Navi Mumbai",
        shortName: "MGM",
        date: "15.04.26",
        post: "LinkedIn, Instagram",
        location: "Navi Mumbai, Maharashtra",
        summary:
            "A high-energy demonstration of Spanda DAT with enthusiastic faculty participation and clinical feedback from leading anesthesiology and emergency medicine experts.",
        highlights:
            "Faculty-led product validation, emergency medicine applications, and collaborative clinical insight sharing.",
        tags: [
            "#MedTech",
            "#HealthTech",
            "#MedicalInnovation",
            "#DigitalHealth",
            "#FutureOfMedicine",
        ],
        gallery: [
            "/images/events/mgm-dat/mgm-dat-01.jpeg",
            "/images/events/mgm-dat/mgm-dat-02.jpeg",
            "/images/events/mgm-dat/mgm-dat-03.jpeg",
        ],
        accent: "from-cyan-500 via-sky-500 to-blue-700",
        isFeatured: true,
    },
    {
        id: "parul",
        name: "Parul Institute of Medical Sciences & Research (PIMSR)",
        shortName: "Parul",
        date: "16.06.26",
        post: "LinkedIn, Instagram",
        location: "Parul University, Gujarat",
        summary:
            "Spanda DAT was demonstrated to faculty and students with strong interest in simulation-based training, live auscultation learning, and future-ready medical education tools.",
        highlights:
            "Collaboration with Delta Healthcare, simulation-based innovation, and Make in India clinical training support.",
        tags: [
            "#SpandaDAT",
            "#MedicalEducation",
            "#ClinicalSkills",
            "#SimulationBasedLearning",
            "#MakeInIndia",
        ],
        gallery: [
            "/images/events/parul/parul-01.jpeg",
            "/images/events/parul/parul-02.png",
        ],
        accent: "from-emerald-500 via-green-500 to-lime-600",
        isFeatured: true,
    },
    {
        id: "namo",
        name: "NAMO Medical Education & Research Institute",
        shortName: "NAMO",
        date: "17.06.26",
        post: "LinkedIn",
        location: "Silvassa, Dadra Nagar Haveli",
        summary:
            "An engaging institutional demonstration highlighting the value of realistic auscultation training, multiple-listener live transmission, and standards-aligned learning experiences.",
        highlights:
            "Dean-level feedback, classroom adoption readiness, and OTA-upgrade ready platform vision.",
        tags: [
            "#SpandaDAT",
            "#MedicalEducation",
            "#SimulationTraining",
            "#ClinicalSkills",
            "#HealthcareInnovation",
        ],
        gallery: [
            "/images/events/namo/namo-01.jpeg",
            "/images/events/namo/namo-02.jpeg",
            "/images/events/namo/namo-03.jpeg",
        ],
        accent: "from-violet-500 via-purple-500 to-indigo-700",
        isFeatured: true,
    },
    {
        id: "rajarajeshwari",
        name: "Rajarajeshwari Medical College and Hospital",
        shortName: "Rajarajeshwari",
        date: "10.08.26",
        post: "LinkedIn",
        location: "Bengaluru, Karnataka",
        summary:
            "A strong faculty interaction focused on interactive clinical skills education, realistic simulation learning, and multi-user auscultation assessments.",
        highlights:
            "Physiology faculty engagement, skill lab collaboration, and immersive training workflows.",
        tags: [
            "#SpandaDAT",
            "#DigitalAuscultationTrainer",
            "#MedicalEducation",
            "#ClinicalSkills",
            "#MakeInIndia",
        ],
        gallery: [
            "/images/events/rajarajeshwari/rajarajeshwari-01.jpeg",
            "/images/events/rajarajeshwari/rajarajeshwari-02.jpeg",
        ],
        accent: "from-amber-500 via-orange-500 to-rose-500",
        isFeatured: true,
    },
    {
        id: "dyp-pune",
        name: "D Y Patil (Pune)",
        shortName: "DY Patil",
        date: "30.03.26",
        post: "No Post",
        location: "Pune, Maharashtra",
        summary:
            "A planned institution demo and event focused on clinical skill enhancement, future-ready training workflows, and medical education innovation.",
        highlights:
            "Upcoming demonstration opportunity with continued focus on medical simulation and digital auscultation education.",
        tags: ["#UpcomingEvent", "#MedicalEducation", "#SpandaDAT"],
        gallery: [],
        accent: "from-slate-500 via-slate-600 to-slate-800",
        isFeatured: false,
    },
];

const stats = [
    { label: "Institutions", value: "5" },
    { label: "Live demos", value: "4" },
    { label: "Upcoming", value: "1" },
];

export default function EventShowcase() {
    const featuredEvents = useMemo(
        () => {
            try {
                if (!Array.isArray(events)) return [];
                return events.filter((event) => Array.isArray(event.gallery) && event.gallery.length > 0);
            } catch (err) {
                console.error("Error filtering featured events:", err);
                return [];
            }
        },
        [],
    );
    const [activeSlide, setActiveSlide] = useState(0);
    const currentEvent = (Array.isArray(featuredEvents) && featuredEvents.length > 0 ? featuredEvents[activeSlide] : null) ?? (Array.isArray(events) && events.length > 0 ? events[0] : null);

    const handlePrev = () => {
        try {
            if (!Array.isArray(featuredEvents) || featuredEvents.length === 0) return;
            setActiveSlide((prev) =>
                (prev === 0 ? featuredEvents.length - 1 : prev - 1),
            );
        } catch (err) {
            console.error("handlePrev error:", err);
        }
    };

    const handleNext = () => {
        try {
            if (!Array.isArray(featuredEvents) || featuredEvents.length === 0) return;
            setActiveSlide((prev) =>
                (prev === featuredEvents.length - 1 ? 0 : prev + 1),
            );
        } catch (err) {
            console.error("handleNext error:", err);
        }
    };

    return (
        <div className="bg-[#f6f9ff] pb-20 pt-4 text-slate-800">
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(74,108,247,0.18),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(50,183,72,0.18),_transparent_35%)]" />
                <div className="container relative mx-auto px-4 py-10 md:py-16">
                    <div className="grid items-center gap-8 lg:grid-cols-[1.2fr_0.8fr]">
                        <div>
                            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-saintiantblue shadow-sm backdrop-blur-sm">
                                <Sparkles className="h-4 w-4 text-saintiantgreen" />
                                Spanda DAT Showcase
                            </div>
                            <h1 className="max-w-xl text-4xl font-black leading-tight text-saintiantblue md:text-5xl">
                                Medical education events that bring auscultation learning to life.
                            </h1>
                            <p className="mt-5 max-w-xl text-base leading-7 text-slate-600 md:text-lg">
                                From faculty workshops to institutional demonstrations, we showcase how Spanda DAT transforms clinical skills training through immersive, technology-driven learning.
                            </p>
                            <div className="mt-7 flex flex-wrap items-center gap-3">
                                <a
                                    href="#featured-events"
                                    className="inline-flex items-center gap-2 rounded-full bg-saintiantblue px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-900/20 transition hover:-translate-y-0.5 hover:bg-[#1a2d62]"
                                >
                                    View events
                                    <ArrowRight className="h-4 w-4" />
                                </a>
                                <a
                                    href="#college-gallery"
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition hover:border-saintiantgreen hover:text-saintiantblue"
                                >
                                    Explore gallery
                                </a>
                            </div>
                        </div>

                        {/* <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
                            {Array.isArray(stats) && stats.length > 0 ? stats.map((stat) => (
                                <div
                                    key={stat.label}
                                    className="rounded-3xl border border-white/60 bg-white p-5 shadow-[0_20px_45px_rgba(15,23,42,0.08)]"
                                >
                                    <div className="text-3xl font-black text-saintiantblue">
                                        {stat.value}
                                    </div>
                                    <div className="mt-2 text-sm font-medium uppercase tracking-[0.18em] text-slate-500">
                                        {stat.label}
                                    </div>
                                </div>
                            )) : (
                                Array.from({length:3}).map((_,i)=><div key={i} className="animate-pulse rounded-3xl bg-slate-200 h-24"/>)
                            )}
                        </div> */}
                    </div>
                </div>
            </section>

            <section id="featured-events" className="container relative mx-auto px-4 py-12 md:py-16">
                <div className="mb-8 flex items-end justify-between gap-4">
                    <div>
                        <p className="mb-2 text-sm font-semibold uppercase tracking-[0.24em] text-saintiantgreen">
                            Featured event
                        </p>
                        <h2 className="text-3xl font-black text-saintiantblue md:text-4xl">
                            Institution highlight
                        </h2>
                    </div>
                    <div className="hidden items-center gap-2 md:flex">
                        <button
                            type="button"
                            onClick={handlePrev}
                            className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-saintiantblue transition hover:border-saintiantblue hover:bg-slate-50"
                            aria-label="Previous event"
                        >
                            ←
                        </button>
                        <button
                            type="button"
                            onClick={handleNext}
                            className="flex h-11 w-11 items-center justify-center rounded-full bg-saintiantblue text-white transition hover:bg-[#1a2d62]"
                            aria-label="Next event"
                        >
                            →
                        </button>
                    </div>
                </div>

                {currentEvent ? (
                    <div className="grid gap-6 lg:grid-cols-[1.35fr_0.65fr]">
                        <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_30px_70px_rgba(15,23,42,0.08)]">
                            <div className="relative aspect-[16/10] w-full overflow-hidden bg-slate-100">
                                {Array.isArray(currentEvent.gallery) && currentEvent.gallery.length > 0 ? (
                                    <Image
                                        src={currentEvent.gallery[0]}
                                        alt={`${currentEvent.name} - featured event image`}
                                        fill
                                        priority
                                        sizes="(max-width: 1024px) 100vw, 60vw"
                                        className="object-cover"
                                        quality={80}
                                    />
                                ) : (
                                    <div className="flex h-full items-center justify-center bg-gradient-to-br from-slate-200 to-slate-100 text-xl font-bold text-slate-500">
                                        No event gallery yet
                                    </div>
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-900/20 to-transparent" />
                                <div className="absolute left-6 top-6 rounded-full bg-white/90 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-saintiantblue backdrop-blur-sm">
                                    {currentEvent.shortName}
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 p-6 text-white md:p-8">
                                    <div className="mb-2 flex flex-wrap items-center gap-3 text-xs font-medium uppercase tracking-[0.2em] text-slate-100">
                                        <span className="inline-flex items-center gap-2">
                                            <CalendarDays className="h-4 w-4" />
                                            {currentEvent.date}
                                        </span>
                                        <span className="inline-flex items-center gap-2">
                                            <MapPin className="h-4 w-4" />
                                            {currentEvent.location}
                                        </span>
                                    </div>
                                    <h3 className="text-2xl font-black leading-tight md:text-3xl">
                                        {currentEvent.name}
                                    </h3>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-[2rem] border border-slate-200 bg-slate-950 p-6 text-white shadow-[0_30px_70px_rgba(15,23,42,0.18)] md:p-8">
                            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-200">
                                <TrendingUp className="h-4 w-4 text-saintiantgreen" />
                                Event snapshot
                            </div>
                            <h3 className="text-2xl font-black text-white">{currentEvent.name}</h3>
                            <p className="mt-4 text-sm leading-7 text-slate-300">{currentEvent.summary}</p>
                            <div className="mt-6 space-y-3 text-sm">
                                <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                                    <span className="text-slate-300">Platform</span>
                                    <span className="font-semibold text-white">{currentEvent.post}</span>
                                </div>
                                <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                                    <span className="text-slate-300">Date</span>
                                    <span className="font-semibold text-white">{currentEvent.date}</span>
                                </div>
                            </div>
                            <div className="mt-6">
                                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-300">
                                    Highlights
                                </p>
                                <p className="mt-3 text-sm leading-7 text-slate-200">
                                    {currentEvent.highlights}
                                </p>
                            </div>
                            {/* <div className="mt-6 flex flex-wrap gap-2">
                                {Array.isArray(currentEvent.tags) && currentEvent.tags.length > 0 ? currentEvent.tags.map((tag) => (
                                    <span
                                        key={tag}
                                        className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-slate-100"
                                    >
                                        {tag}
                                    </span>
                                )) : null}
                            </div> */}
                        </div>
                    </div>
                ) : (
                    <div className="grid gap-6 lg:grid-cols-[1.35fr_0.65fr]">
                        <div className="animate-pulse h-[400px] bg-slate-200 rounded-[2rem]" />
                        <div className="animate-pulse h-[400px] bg-slate-200 rounded-[2rem]" />
                    </div>
                )}
            </section>

            <section id="college-gallery" className="container relative mx-auto px-4 py-8 md:py-12">
                <div className="mb-8 flex items-center justify-between gap-4">
                    <div>
                        <p className="mb-2 text-sm font-semibold uppercase tracking-[0.24em] text-saintiantgreen">
                            College gallery
                        </p>
                        <h2 className="text-3xl font-black text-saintiantblue md:text-4xl">
                            Demo & event archive
                        </h2>
                    </div>
                </div>

                <div className="grid gap-6 xl:grid-cols-2">
                    {Array.isArray(events) && events.length > 0 ? events.map((event) => (
                        <article
                            key={event.id}
                            className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_20px_40px_rgba(15,23,42,0.06)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_28px_50px_rgba(15,23,42,0.10)]"
                        >
                            <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                                {Array.isArray(event.gallery) && event.gallery.length > 0 ? (
                                    <Image
                                        src={event.gallery[0]}
                                        alt={`${event.name} - event gallery image`}
                                        fill
                                        sizes="(max-width: 1280px) 100vw, 50vw"
                                        className="object-cover transition duration-500 hover:scale-105"
                                        loading="lazy"
                                        quality={75}
                                    />
                                ) : (
                                    <div className="flex h-full items-center justify-center bg-gradient-to-br from-slate-200 via-white to-slate-100 text-xl font-bold text-slate-500">
                                        No post yet
                                    </div>
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-900/20 to-transparent" />
                                <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-white/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-saintiantblue backdrop-blur-sm">
                                    {event.shortName}
                                </div>
                            </div>

                            <div className="p-6">
                                <div className="mb-3 flex flex-wrap items-center gap-3 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                                    <span className="inline-flex items-center gap-2">
                                        <CalendarDays className="h-3.5 w-3.5" />
                                        {event.date}
                                    </span>
                                    <span className="inline-flex items-center gap-2">
                                        <MapPin className="h-3.5 w-3.5" />
                                        {event.location}
                                    </span>
                                </div>
                                <h3 className="text-2xl font-black leading-tight text-saintiantblue">
                                    {event.name}
                                </h3>
                                <p className="mt-4 text-sm leading-7 text-slate-600">{event.summary}</p>

                                {/* <div className="mt-5 flex items-center gap-3 border-t border-slate-100 pt-4">
                                    <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-saintiantblue">
                                        <GraduationCap className="h-3.5 w-3.5" />
                                        {event.post}
                                    </div>
                                </div> */}

                                {/* <div className="mt-5 flex flex-wrap gap-2">
                                    {Array.isArray(event.tags) && event.tags.length > 0 ? event.tags.slice(0, 3).map((tag) => (
                                        <span
                                            key={tag}
                                            className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-700"
                                        >
                                            {tag}
                                        </span>
                                    )) : null}
                                </div> */}
                            </div>
                        </article>
                    )) : (
                        Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="animate-pulse rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                                <div className="h-48 bg-slate-200 rounded-2xl mb-4" />
                                <div className="h-6 bg-slate-200 rounded w-3/4 mb-3" />
                                <div className="h-4 bg-slate-200 rounded w-full" />
                            </div>
                        ))
                    )}
                </div>
            </section>

            <section className="container relative mx-auto px-4 pt-10">
                <div className="rounded-[2rem] border border-slate-200 bg-gradient-to-r from-saintiantblue via-[#18408e] to-saintiantgreen p-8 text-white shadow-[0_30px_70px_rgba(19,34,75,0.25)] md:p-12">
                    <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-blue-100">
                                Spanda DAT
                            </p>
                            <h3 className="text-3xl font-black md:text-4xl">
                                Digital auscultation training built for modern clinical learning.
                            </h3>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <a
                                href="/contact"
                                className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-saintiantblue transition hover:bg-slate-100"
                            >
                                Book a demo
                                <ArrowRight className="h-4 w-4" />
                            </a>
                            <a
                                href="/productDAT"
                                className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-transparent px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/5"
                            >
                                Explore product
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
